"use client";

import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from "react";

import { RenewalSectionType } from "@/types/renewals";

interface AppContextType {
  expandedSections: RenewalSectionType[];
  setExpandedSections: (section: RenewalSectionType[]) => void;
  isLoading: boolean;
  isSelectItemByLeftPanel: boolean;
  setIsSelectItemByLeftPanel: (isSelectItemByLeftPanel: boolean) => void;
  updateLastEditAccordion: (section: RenewalSectionType) => void;
}

const RenewalStateContext = createContext<AppContextType>({
  expandedSections: [],
  setExpandedSections: () => {},
  isLoading: true,
  isSelectItemByLeftPanel: false,
  setIsSelectItemByLeftPanel: () => {},
  updateLastEditAccordion: () => {},
});

type RenewalStateProviderProps = {
  children: React.ReactNode;
  isLoading: boolean;
};

function RenewalStateProvider({
  children,
  isLoading,
}: Readonly<RenewalStateProviderProps>) {
  const [expandedSections, setExpandedSections] = useState<
    RenewalSectionType[]
  >([]);
  const [isSelectItemByLeftPanel, setIsSelectItemByLeftPanel] =
    useState<boolean>(false);

  useEffect(() => {
    if (!sessionStorage) return;

    const data = sessionStorage.getItem("expandedSections");
    const parsedData = data ? JSON.parse(data) : [];

    if (parsedData && parsedData.length > 0) {
      setExpandedSections(parsedData);
      setIsSelectItemByLeftPanel(true);
    } else {
      setExpandedSections(["policy-details"]);
    }
  }, []);

  const updateLastEditAccordion = (section: RenewalSectionType) => {
    const newActiveArr = [
      ...expandedSections.filter((item) => item !== section),
      section,
    ];

    setExpandedSections(newActiveArr);
    sessionStorage.setItem("expandedSections", JSON.stringify(newActiveArr));
  };

  const value = useMemo(
    () => ({
      isLoading,
      expandedSections,
      setExpandedSections,
      isSelectItemByLeftPanel,
      setIsSelectItemByLeftPanel,
      updateLastEditAccordion,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, expandedSections, isSelectItemByLeftPanel]
  );

  return (
    <RenewalStateContext.Provider value={value}>
      {children}
    </RenewalStateContext.Provider>
  );
}

export const useRenewalStateContext = () => useContext(RenewalStateContext);

export default RenewalStateProvider;
